import React, { Component } from "react";
import {
  Box,
  Card,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { createRoot } from "react-dom/client";

import LoginBanner from "./LoginBanner";
import MFALoginForm from "./MFALoginForm";

import { bizpost } from "../../ajax";
import sonabhyLogo from "../../resources/sonabhy.png";

export type LoginStep = "CREDENTIALS" | "OTP_SELECT" | "OTP_VERIFY";
export type OtpChannel = "EMAIL" | "SMS";

const localTheme = createTheme({
  palette: {
    primary: {
      main: "#116B43",
      dark: "#0B4F2D",
    },
    secondary: {
      main: "#163A5F",
    },
    background: {
      default: "#F4F7F8",
      paper: "#FFFFFF",
    },
  },
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 20px 60px rgba(14, 30, 37, 0.10)",
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 10,
          paddingInline: 14,
        },
      },
    },
  },
});

interface Props {
  home: any;
  apptheme?: any;
  onMessage?: (type: string, title: string, message: string) => void;
  onForgotPassword?: () => void;
}

interface State {
  lang: "FR" | "EN";
  step: LoginStep;
  email: string;
  password: string;
  otp: string;
  otpChannel: OtpChannel;
  loading: boolean;
  resendCountdown: number;
  passwordErrors: string[];
  showPassword: boolean;
}

export default class MFALoginMain extends Component<any, State> {
  private timer?: ReturnType<typeof setInterval>;

  state: State = {
    lang: "FR",
    step: "CREDENTIALS",
    email: "",
    password: "",
    otp: "",
    otpChannel: "EMAIL",
    loading: false,
    resendCountdown: 0,
    passwordErrors: [],
    showPassword: false,
  };

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  private notify(type: string, title: string, message: string) {
    if (this.props.onMessage) {
      this.props.onMessage(type, title, message);
      return;
    }
    window.alert(`${title}: ${message}`);
  }

  private validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push("Au moins 8 caractères");
    if (!/[A-Z]/.test(password)) errors.push("Au moins une majuscule");
    if (!/[a-z]/.test(password)) errors.push("Au moins une minuscule");
    if (!/[0-9]/.test(password)) errors.push("Au moins un chiffre");
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=/\\[\];'`~]/.test(password)) {
      errors.push("Au moins un caractère spécial");
    }
    return errors;
  };

  private startResendTimer = () => {
    if (this.timer) clearInterval(this.timer);
    this.setState({ resendCountdown: 60 });

    this.timer = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendCountdown <= 1) {
          if (this.timer) clearInterval(this.timer);
          return { resendCountdown: 0 };
        }
        return { resendCountdown: prev.resendCountdown - 1 };
      });
    }, 1000);
  };

  private handleCredentialSubmit = async () => {
    const { email, password } = this.state;
    this.setState({ loading: true });

    try {
      const response = await bizpost({
        s: "security",
        a: "logon",
        unm: email,
        eml: email,
        pwd: password,
      });

      if (!response?.success) {
        const err = response?.ed || {};
        this.notify(
          err.type || "warn",
          err.title || "Erreur de connexion",
          err.message || response?.em || "Échec de la connexion."
        );
        return;
      }

      this.setState({ step: "OTP_SELECT" });
    } catch (error: any) {
      this.notify(
        error?.type || "error",
        error?.title || "Erreur de connexion",
        error?.message || "La demande de connexion a échoué."
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  private handleSendOtp = async () => {
    const { email, otpChannel } = this.state;
    this.setState({ loading: true });

    try {
      const response = await bizpost({
        s: "security",
        a: "otp",
        unm: email,
        eml: email,
        chn: otpChannel,
      });

      if (!response?.success) {
        const err = response?.ed || {};
        this.notify(
          err.type || "error",
          err.title || "Erreur OTP",
          err.message || response?.em || "Échec de l’envoi du code."
        );
        return;
      }

      this.setState({ step: "OTP_VERIFY", otp: "" });
      this.startResendTimer();
    } catch (error: any) {
      this.notify(
        error?.type || "error",
        error?.title || "Erreur OTP",
        error?.message || "Échec de l’envoi du code OTP."
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  private handleVerifyOtp = async () => {
    const { home, apptheme } = this.props;
    const { lang, otp } = this.state;
    this.setState({ loading: true });

    try {
      const response = await bizpost({
        s: "security",
        a: "verify",
        otp,
        lang,
      });

      if (!response?.success) {
        const err = response?.ed || {};
        this.notify(
          err.type || "error",
          err.title || "Erreur de vérification",
          err.message || response?.em || "Code de vérification invalide."
        );
        return;
      }

      const container = document.getElementById("app");
      if (!container) throw new Error("App container not found");

      const root = createRoot(container);
      root.render(
        <ThemeProvider theme={apptheme || localTheme}>
          {React.createElement(home, { udata: response })}
        </ThemeProvider>
      );
    } catch (error: any) {
      this.notify(
        error?.type || "error",
        error?.title || "Erreur de vérification",
        error?.message || "La vérification OTP a échoué."
      );
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const {
      lang,
      step,
      email,
      password,
      otp,
      otpChannel,
      loading,
      resendCountdown,
      passwordErrors,
      showPassword,
    } = this.state;

    return (
      <ThemeProvider theme={localTheme}>
        <Box
          sx={{
            //minHeight: "100dvh",
            bgcolor: "#F4F7F8",
            background:
              "linear-gradient(180deg, #F8FAFB 0%, #EEF3F5 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2 },
            //overflow: "auto",
          }}
        >
          <Card
            sx={{
              width: "100%",
              maxWidth: 1100,
              minHeight: { lg: "640px" }, // 🔥 fixed clean height
              borderRadius: "12px",
              //overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                //gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gridTemplateColumns: { xs: "1fr", lg: "0.95fr 1.05fr" },
                height: "100%",
              }}
            >
              <Box sx={{ display: { xs: "none", lg: "block" }, height: "100%" }}>
                <LoginBanner logoSrc={sonabhyLogo} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  px: { xs: 2.5, md: 3 },
                }}
              >
                <Box
                  sx={{
                    //px: { xs: 3, sm: 4, md: 5 },
                    //pt: { xs: 3, md: 4 },
                    //pb: { xs: 2, md: 2 },
                    px: { xs: 2.5, sm: 3, md: 3.5 },
                    pt: { xs: 2.5, md: 3 },
                    pb: { xs: 1.5, md: 1.5 },
                    borderBottom: "1px solid #EEF2F5",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        component="img"
                        src={sonabhyLogo}
                        alt="SONABHY"
                        sx={{
                          width: 44,
                          height: 44,
                          objectFit: "contain",
                        }}
                      />
                      <Stack spacing={0.25}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            letterSpacing: 1.1,
                            color: "#8A9BAB",
                            fontWeight: 800,
                          }}
                        >
                          Portail interne
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 15,
                            color: "#17324D",
                            fontWeight: 700,
                          }}
                        >
                          BPMMS · SONABHY
                        </Typography>
                      </Stack>
                    </Stack>

                    <ToggleButtonGroup
                      size="small"
                      value={lang}
                      exclusive
                      onChange={(_, value) => value && this.setState({ lang: value })}
                    >
                      <ToggleButton value="FR">FR</ToggleButton>
                      <ToggleButton value="EN">EN</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    px: { xs: 3, sm: 3, md: 4 },
                    py: { xs: 2, md: 3 },
                  }}
                >
                  <Box sx={{ width: "100%", maxWidth: 500 }}>
                    <Stack spacing={1.25} sx={{ mb: 3 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 26, md: 30 },
                          lineHeight: 1.2,
                          fontWeight: 800,
                          color: "#17324D",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {lang === "FR" ? "Connexion sécurisée" : "Secure sign in"}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 15,
                          color: "#66788A",
                          lineHeight: 1.7,
                        }}
                      >
                        {lang === "FR"
                          ? "Accédez au système intégré de budgétisation, d’exécution et de performance."
                          : "Access the integrated budgeting, execution and performance platform."}
                      </Typography>
                    </Stack>

                    <MFALoginForm
                      step={step}
                      email={email}
                      password={password}
                      otp={otp}
                      otpChannel={otpChannel}
                      loading={loading}
                      resendCountdown={resendCountdown}
                      passwordErrors={passwordErrors}
                      showPassword={showPassword}
                      onEmailChange={(value:any) => this.setState({ email: value })}
                      onPasswordChange={(value:any) =>
                        this.setState({
                          password: value,
                          passwordErrors: this.validatePassword(value),
                        })
                      }
                      onOtpChange={(value:any) => this.setState({ otp: value })}
                      onOtpChannelChange={(value:any) => this.setState({ otpChannel: value })}
                      onTogglePassword={() =>
                        this.setState((prev) => ({ showPassword: !prev.showPassword }))
                      }
                      onCredentialSubmit={this.handleCredentialSubmit}
                      onSendOtp={this.handleSendOtp}
                      onVerifyOtp={this.handleVerifyOtp}
                      onBackToCredentials={() =>
                        this.setState({ step: "CREDENTIALS", otp: "" })
                      }
                      onMessage={this.props.onMessage}
                      onForgotPassword={this.props.onForgotPassword}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>
      </ThemeProvider>
    );
  }
}
